import { Component, ElementRef, EventEmitter, HostListener, OnDestroy, Output, ViewChild, forwardRef } from '@angular/core';
import { AbstractControl, ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator } from '@angular/forms';
import { LoggingService } from '@app-core';
import { SignaturePad, SignaturePadModule } from 'angular-signature-pad-v2';

@Component({
    selector: 'app-signature-pad',
    imports: [SignaturePadModule],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => SignaturePadComponent),
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => SignaturePadComponent),
            multi: true,
        },
    ],
    templateUrl: './signature-pad.component.html',
    styleUrl: './signature-pad.component.scss'
})
export class SignaturePadComponent implements ControlValueAccessor, Validator, OnDestroy {
    @ViewChild(SignaturePad) signaturePad: SignaturePad;
    signatureDataURL: string | null = null;
    isAccepted: boolean = false;
    isTouched: boolean = false;
    private onChange: (signature: string | null) => void = () => {};
    private onTouched: () => void = () => {};
    @Output() onBeginEvent = new EventEmitter<void>();
    @Output() onEndEvent = new EventEmitter<void>();
    private resizeObserver: ResizeObserver;
    private readonly canvasAspectRatio = 0.5; // Height is 50% of width by default

    public signaturePadOptions: { minWidth: number; canvasWidth: number; canvasHeight: number; backgroundColor: string; penColor: string } = {
        minWidth: 2,
        canvasWidth: 200,
        canvasHeight: 200,
        backgroundColor: 'rgb(255, 255, 255)',
        penColor: 'rgb(0, 0, 0)',
    };

    constructor(private readonly elementRef: ElementRef) {}

    ngAfterViewInit() {
        this.signaturePad.clear();
        this.updateCanvasSize();

        // Set up ResizeObserver to respond to container size changes
        this.resizeObserver = new ResizeObserver(() => {
            this.updateCanvasSize();
        });

        // Observe the container element
        const container = this.elementRef.nativeElement.querySelector('.signature-pad-container');
        if (container) {
            this.resizeObserver.observe(container);
        }
    }

    ngOnDestroy() {
        // Clean up the observer when component is destroyed
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    }

    writeValue(signature: string | null): void {
        this.signatureDataURL = signature;
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    clearSignature(): void {
        this.signatureDataURL = null;
        this.isAccepted = false;
        this.onChange(null);
        this.signaturePad.clear();
    }

    acceptSignature(): void {
        if (this.signatureDataURL) {
            this.isAccepted = true;
            this.onChange(this.signatureDataURL);
        }
    }

    setDisabledState?(isDisabled: boolean): void {
        if (this.signaturePad) {
            this.signaturePad.off();
            if (!isDisabled) {
                this.signaturePad.on();
            }
        }
    }

    validate(control: AbstractControl): ValidationErrors | null {
        if (!this.signatureDataURL) {
            return { signatureRequired: true };
        }
        if (!this.isAccepted) {
            return { signatureNotAccepted: true };
        }
        return null;
    }

    drawComplete() {
        LoggingService.log('drawComplete', this.signaturePad.toDataURL());
        this.signatureDataURL = this.signaturePad.toDataURL();
        this.isAccepted = false;
        this.onChange(this.signatureDataURL);
    }

    drawStart() {
        LoggingService.log('drawStart');
        this.isTouched = true;
        this.onTouched();
    }

    testSignaturePad() {
        if (this.signaturePad) {
            console.log('SignaturePad instance:', this.signaturePad);
            console.log('Is empty:', this.signaturePad.isEmpty());
        } else {
            console.error('SignaturePad is not initialized');
        }
    }

    // Listen for window resize events as a fallback
    @HostListener('window:resize')
    onResize() {
        this.updateCanvasSize();
    }

    /**
     * Updates the canvas size based on the container's width
     * This makes the signature pad fully responsive to its container
     */
    private updateCanvasSize(): void {
        if (!this.signaturePad) return;

        // Get container element and its computed width
        const container = this.elementRef.nativeElement.querySelector('.signature-pad-container');
        if (!container) return;

        // Use a fixed padding of 10px on each side instead of calculating from styles
        const horizontalPadding = 20; // 10px left + 10px right

        // Calculate the inner width of the container with minimal padding
        const availableWidth = container.clientWidth - horizontalPadding;

        // Set minimum width for very small screens
        const newWidth = Math.max(availableWidth, 200);
        const newHeight = Math.round(newWidth * this.canvasAspectRatio);

        // Save current signature if it exists
        let dataURL = null;
        if (this.signaturePad && !this.signaturePad.isEmpty()) {
            dataURL = this.signaturePad.toDataURL();
        }

        // Update options with new dimensions
        this.signaturePadOptions = {
            ...this.signaturePadOptions,
            canvasWidth: newWidth,
            canvasHeight: newHeight,
        };

        // Access the canvas element and update its dimensions
        const canvas = (this.signaturePad as any)?.elementRef?.nativeElement?.querySelector('canvas');
        if (canvas) {
            canvas.width = newWidth;
            canvas.height = newHeight;

            // Add styles to center the canvas in its container if needed
            canvas.style.margin = '0 auto';
            canvas.style.display = 'block';

            // If we had a signature, restore it
            if (dataURL) {
                setTimeout(() => {
                    this.signaturePad.fromDataURL(dataURL);
                }, 0);
            }
        }
    }
}
